import {
  Currency,
  Payable,
  PayableStatus,
  PaymentMethodType
} from "@wingspanhq/wingspan-api-sdk";
import { selectorCounterpartyDisplayName } from "../../../shared/selectors/selectorCounterpartyDisplayName";
import { ClientPayableDisplayStatus, ClientPayableView } from "../types";

const isPastDue = (payable: Payable): boolean =>
  !!payable.dueDate && payable.dueDate.getTime() < Date.now();

const selectorClientPayableDisplayStatus = (
  payable: Payable
): ClientPayableDisplayStatus => {
  switch (payable.status) {
    case PayableStatus.OPENED:
    case PayableStatus.PENDING:
      return isPastDue(payable) ? "overdue" : "unpaid";
    case PayableStatus.PAYMENT_IN_TRANSIT:
      return "inTransit";
    case PayableStatus.PAID:
    case PayableStatus.PARTIALLY_PAID:
    case PayableStatus.PAID_OFF_PLATFORM:
      return "paid";
    case PayableStatus.CANCELLED:
      return "cancelled";
    case PayableStatus.REFUNDED:
    case PayableStatus.PARTIALLY_REFUNDED:
      return "refunded";
    default:
      return "unpaid";
  }
};

/**
 * Normalizes a v3 `Payable` (fetched with the `payee` expansion) onto the
 * source-agnostic `ClientPayableView` the detail screen's presentational
 * pieces render. Only producer today; a future guest sibling would add its
 * own selector from `InvoicePaymentIntent.invoice` onto the same shape.
 */
export const selectorClientPayableView = (
  payable: Payable
): ClientPayableView => {
  const status = selectorClientPayableDisplayStatus(payable);

  return {
    id: payable.id ?? "",
    externalId: payable.externalId ?? undefined,
    status,
    amount: payable.amount ?? 0,
    currency: (payable.currency as Currency) ?? Currency.USD,
    lineItems: (payable.lineItems ?? []).map(lineItem => ({
      description: lineItem.description ?? "",
      amount: lineItem.totalCost ?? 0
    })),
    dueDate: payable.scheduledPaymentDate ?? payable.dueDate ?? undefined,
    paidAt: payable.events?.paidAt ?? undefined,
    payeeName: payable.payee
      ? selectorCounterpartyDisplayName(payable.payee)
      : (payable.payeeId ?? ""),
    payeeEmail: payable.payee?.email ?? undefined,
    notes: payable.notes ?? undefined,
    canPay: status === "unpaid" || status === "overdue",
    preferredFundingSource: selectorPreferredFundingSource(payable)
  };
};

const selectorPreferredFundingSource = (
  payable: Payable
): ClientPayableView["preferredFundingSource"] => {
  const defaultPaymentMethod = payable.payee?.defaultPaymentMethod;
  if (!defaultPaymentMethod?.id) {
    return undefined;
  }

  if (defaultPaymentMethod.type === PaymentMethodType.EXTERNAL_BANK_ACCOUNT) {
    return { type: "ExternalBankAccount", id: defaultPaymentMethod.id };
  }
  if (defaultPaymentMethod.type === PaymentMethodType.PAYMENT_CARD) {
    return { type: "PaymentCard", id: defaultPaymentMethod.id };
  }
  return undefined;
};
